
import Vue from 'vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import { db, QueryDocumentSnapshot } from '@/firebase';
import _keys from 'lodash/keys';
import _sortBy from 'lodash/sortBy';
import { titleCase } from '@/filters';
import { Company, Team } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { ROUTER } from '@/router/constants';
import { smartFilter } from '@/utils/fuzzySearch';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';
import pluralize from 'pluralize';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import { TeamUserMixin } from '@/mixins';

const PAGE_SIZE = 100;

// Resolves a raw team doc's `company` DocumentReference into the populated company object.
// No clubOffer resolution — this picker doesn't surface plan/benefits info.
async function resolveTeamRefs (rawTeam: Team, teamId: string): Promise<Team> {
	const team: Team = { ...rawTeam, id: teamId };
	if (!team.companyId) {
		team.company = null;
		return team;
	}
	const companyRef = db.doc(`companies/${team.companyId}`);
	const companyData = (await companyRef.get()).data() as Company | undefined;
	team.company = companyData ? { ...companyData, id: companyRef.id } : null;
	return team;
}

export default Vue.extend({
	name: 'AccountSelectionMenuOrBottomSheet',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		nudgeLeft: {
			type: Number,
			default: 0,
		},
		nudgeRight: {
			type: Number,
			default: 0,
		},
		nudgeBottom: {
			type: Number,
			default: 0,
		},
		openOnHover: {
			type: Boolean,
			default: false,
		},
		closeDelay: {
			type: Number,
			default: 200,
		},
	},
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			// Teams loading state — snapshot-based pagination via teams.where(`userIds.${uid}`,'==',true).
			teamsList: [] as Team[],
			lastSnap: null as QueryDocumentSnapshot | null,
			canLoadMoreTeams: false,
			hasLoadedInitial: false,
			loadingTeams: false,
			loadingMoreTeams: false,
			hasTeamsError: false,
			// Team selection state
			pendingTeamDashboardNavigation: null,
			// Search functionality
			searchValue: '',
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'loadingTeam',
			'loadingUser',
		]),
		...mapGetters([
			'displayLabels',
			'activeDomainClaimRestriction',
		]),

		// Current selection state
		runnitsOwnerSelection () {
			return (this.runnitState && this.runnitState.runnitsOwnerSelection) || RUNNITS_OWNER_SELECTION.USER;
		},

		isPersonalSelected () {
			return this.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER;
		},

		currentTeamId () {
			return this._get(this.team, 'id', null);
		},

		// Check if user has teams
		hasTeams () {
			if (!this.user) return false;
			const numTeams = _keys(this.user.teamIds || {}).length;
			return numTeams > 0;
		},

		// Search functionality
		showSearch () {
			return this.teamsList.length > 3;
		},

		filteredTeamsList () {
			if (!this.searchValue) return this.teamsList;

			// Build search keys including custom fields
			const searchKeys = [
				{ name: 'name', weight: 0.6 },
				{ name: 'company.name', weight: 0.4 },
			];

			// Add custom fields to search keys
			const customFieldKeys = this.getCustomFieldSearchKeys();
			searchKeys.push(...customFieldKeys);

			return smartFilter(
				this.teamsList,
				this.searchValue,
				searchKeys
			);
		},

		// Check if teams have multiple different companies
		showCompanyNameSubtext () {
			if (!this.teamsList || this.teamsList.length < 2) return false;

			// Check if any team has a different company ID than the first team
			const firstCompanyId: string = this.teamsList[0]?.companyId;
			return this.teamsList.some((team: Team) => team.companyId && team.companyId !== firstCompanyId);
		},
		/** Personal is affected whenever the signed-in email’s domain is claimed (even if current context hides restriction, e.g. on a matching-company team). */
		isPersonalDomainRestricted (): boolean {
			return !!this.activeDomainClaimRestriction;
		},
		personalDomainRestrictionTooltip (): string {
			return getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team);
		},
	},
	watch: {
		value (newVal) {
			// Load teams the first time the menu opens.
			if (newVal && this.hasTeams && !this.hasLoadedInitial && !this.loadingTeams) {
				this.loadInitialTeams();
			}
		},

		user (newVal, oldVal) {
			// If the user changes (logout/login or admin proxy switch), force a refetch.
			const newId: string = newVal?.id || null;
			const oldId: string = oldVal?.id || null;
			if (newId !== oldId) {
				this.resetTeamsState();
				if (this.value && this.hasTeams) {
					this.loadInitialTeams();
				}
			}
		},

		team (newVal) {
			// Handle team selection completion
			if (newVal && newVal.id) {
				// Close menu after successful team switch
				this.$emit('input', false);

				// Handle pending dashboard navigation
				if (this.pendingTeamDashboardNavigation && this.pendingTeamDashboardNavigation.teamId === newVal.id) {
					const { close } = this.pendingTeamDashboardNavigation;
					this.pendingTeamDashboardNavigation = null;

					// Navigate to team dashboard
					this.routerPush(this.$route, this.$router, {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: newVal.id,
						},
					});

					// Close the menu
					close();
				}
			}
		},
	},
	destroyed () {
		// Clear any pending navigation state
		this.pendingTeamDashboardNavigation = null;
	},
	methods: {
		pluralize,
		titleCase,
		...mapActions([
			'updateToolbar',
			'updateSnackbar',
			'incrementCreateNewTeamTrigger',
			'bindAllTeamData',
		]),

		// Personal account selection
		onPersonalAccountSelect (close) {
			if (this.isPersonalSelected) {
				close();
				return;
			}

			// Switch to personal account
			this.switchBetweenTeamAndUser(RUNNITS_OWNER_SELECTION.USER);

			close();
		},

		// Team selection
		isTeamSelected (team: Team): boolean {
			return !!(
				this.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.currentTeamId === team.id
			);
		},
		isTeamDomainRestricted (team: Team): boolean {
			return !!(
				this.activeDomainClaimRestriction &&
				team?.companyId !== this.activeDomainClaimRestriction.companyId
			);
		},
		getTeamDomainRestrictionTooltip (team: Team): string {
			return this.isTeamDomainRestricted(team)
				? getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team)
				: '';
		},

		async onTeamSelect (team, close) {
			if (this.isTeamSelected(team)) {
				close();
				return;
			}

			try {
				// Switch to team account
				this.switchBetweenTeamAndUser(RUNNITS_OWNER_SELECTION.TEAM);

				// Bind team data
				this.bindAllTeamData(team.id);

				// Note: Menu will close when team watcher fires
			} catch (error) {
				console.error('Error switching to team:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Failed to switch to ${this.displayLabels.team} account`,
					show: true,
				});
			}
		},

		async onNavigateToTeamDashboard (team, close) {
			try {
				// Also close the account menu
				this.updateToolbar({
					accountMenuOpen: false,
				});

				// First ensure the team is selected
				if (!this.isTeamSelected(team)) {
					// Set up pending navigation to be handled by team watcher
					this.pendingTeamDashboardNavigation = {
						teamId: team.id,
						close,
					};

					// Switch to team account
					this.switchBetweenTeamAndUser(RUNNITS_OWNER_SELECTION.TEAM);

					// Bind team data
					this.bindAllTeamData(team.id);

					// Navigation will happen in team watcher when team loads
				} else {
					// Team is already selected, navigate immediately
					this.routerPush(this.$route, this.$router, {
						name: ROUTER.TEAM_DASHBOARD,
						params: {
							teamId: team.id,
						},
					});

					// Close the menu
					close();
				}
			} catch (error) {
				console.error('Error navigating to team dashboard:', error);
				this.pendingTeamDashboardNavigation = null;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Failed to navigate to ${this.displayLabels.team} dashboard`,
					show: true,
				});
			}
		},

		// --- Teams loading (paged .get() against teams.where(`userIds.${uid}`,'==',true)) ---
		resetTeamsState () {
			this.teamsList = [];
			this.lastSnap = null;
			this.canLoadMoreTeams = false;
			this.hasLoadedInitial = false;
			this.hasTeamsError = false;
		},
		async loadInitialTeams () {
			if (!this.user || this.loadingTeams) return;

			this.loadingTeams = true;
			this.hasTeamsError = false;

			try {
				this.resetTeamsState();
				await this.fetchPage();
				this.hasLoadedInitial = true;
			} catch (error) {
				console.error('Error loading teams:', error);
				this.hasTeamsError = true;
			} finally {
				this.loadingTeams = false;
			}
		},

		async loadMoreTeams () {
			if (!this.user || this.loadingMoreTeams || !this.canLoadMoreTeams) return;

			this.loadingMoreTeams = true;

			try {
				await this.fetchPage();
			} catch (error) {
				console.error('Error loading more teams:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error loading more ${pluralize(this.displayLabels.team)}`,
					show: true,
				});
			} finally {
				this.loadingMoreTeams = false;
			}
		},

		// Fetches one page of teams (PAGE_SIZE docs), resolves company refs, and appends to
		// `teamsList`. Pagination uses the QueryDocumentSnapshot cursor (`lastSnap`) — required
		// because we can't orderBy (the `userIds.${uid}` filter is on a dynamic field path with
		// no composite index). Display sort by name happens in memory.
		async fetchPage () {
			if (!this.user?.id) return;
			let ref = db
				.collection('teams')
				.where(`userIds.${this.user.id}`, '==', true)
				.limit(PAGE_SIZE);
			if (this.lastSnap) ref = ref.startAfter(this.lastSnap);

			const snap = await ref.get();
			if (snap.empty) {
				this.canLoadMoreTeams = false;
				return;
			}

			const newTeams: Team[] = await Promise.all(
				snap.docs.map((d: QueryDocumentSnapshot) => resolveTeamRefs(d.data() as Team, d.id)),
			);

			// Dedupe defensively (snapshot cursors shouldn't overlap, but cheap to guard).
			const existing = new Set(this.teamsList.map((t: Team) => t.id));
			const additions = newTeams.filter((t: Team) => !existing.has(t.id));
			this.teamsList = _sortBy(this.teamsList.concat(additions), [(t: Team) => (t.name || '').toLowerCase()]);

			this.lastSnap = snap.docs[snap.docs.length - 1];
			this.canLoadMoreTeams = snap.docs.length === PAGE_SIZE;
		},

		retryLoadTeams () {
			this.loadInitialTeams();
		},

		// User-triggered refresh: drop everything and re-fetch page 1.
		async onRefreshTeams () {
			if (this.loadingTeams || this.loadingMoreTeams) return;
			await this.loadInitialTeams();
		},

		// Utility methods
		getTeamInitials (teamName) {
			if (!teamName) return this.displayLabels.team.charAt(0).toUpperCase();
			const firstTwoLetters = teamName.slice(0, 2);
			return firstTwoLetters.charAt(0).toUpperCase() + firstTwoLetters.slice(1).toLowerCase();
		},

		getTeamName (teamName) {
			if (!teamName) return `${titleCase(this.displayLabels.team)}`;
			return teamName.length > 20 ? teamName.substring(0, 23) + '...' : teamName;
		},

		// New team creation
		async onCreateNewTeam (close) {
			try {
				// Close the account selection menu
				close();

				// Navigate to the company dashboard
				await this.routerPush(this.$route, this.$router, {
					name: ROUTER.COMPANY_DASHBOARD,
				});

				// Wait for navigation to complete, then trigger new team creation via Vuex
				this.$nextTick(() => {
					this.incrementCreateNewTeamTrigger();
				});
			} catch (error) {
				console.error('Error navigating to create new team:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Failed to navigate to ${this.displayLabels.team} creation`,
					show: true,
				});
			}
		},

		/**
		 * Generates search keys for team custom fields based on company definitions
		 */
		getCustomFieldSearchKeys () {
			const customFieldKeys = [];
			const processedCompanies = new Set();

			// Iterate through teams to find all unique companies and their custom field definitions
			this.teamsList.forEach(team => {
				if (team.company && !processedCompanies.has(team.company.id)) {
					processedCompanies.add(team.company.id);

					// Add search keys for each custom field defined by the company
					if (team.company.teamCustomFieldDefs) {
						team.company.teamCustomFieldDefs.forEach(fieldDef => {
							customFieldKeys.push({
								name: `customFields.${fieldDef.key}`,
								weight: 0.2, // Lower weight for custom fields
							});
						});
					}
				}
			});

			return customFieldKeys;
		},
	},
	components: {
		MenuOrBottomSheet,
		BaseButton,
		GlassButton,
	},
});
